import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { format, parse } from 'date-fns'; // Import date-fns functions
import { Edit2, Trash2 } from 'lucide-react';
import {
  PrimaryButton,
  SecondaryButton, // Keep SecondaryButton for Edit/Cancel
  DangerButton, // Add a DangerButton style for Delete
  FormInput,
  FormTextArea
} from '@shared/forms/FormStyles';
import ValidationError from '@shared/feedback/ValidationError'; // Import the extracted ValidationError component

const ExtractedRoleCard = ({ role, onEdit, onValidate, onDelete }) => { // Add onDelete prop
  const [isEditing, setIsEditing] = useState(false);
  const [editedRole, setEditedRole] = useState(role);
  const [validationErrors, setValidationErrors] = useState([]);

  // Function to validate the role data during editing
  const validateRole = () => {
    const errors = [];
    if (!editedRole.company?.trim()) errors.push("Company name is required");
    if (!editedRole.role?.trim()) errors.push("Role title is required");
    if (!editedRole.startDate?.trim()) errors.push("Start date is required");
    // Role Overview (roleDescription) is not strictly required for saving, but good practice
    // if (!editedRole.description?.trim()) errors.push("Role Overview is required"); // Use 'description'
    if (!editedRole.responsibilities || editedRole.responsibilities.length === 0) errors.push("At least one Responsibility is required");

    // Validate date logic if both dates are present
    if (editedRole.startDate && editedRole.endDate) {
        try {
            const startDate = new Date(editedRole.startDate + "-01"); // Append day for Date object
            const endDate = new Date(editedRole.endDate + "-01");
             if (endDate < startDate) {
                errors.push("End date cannot be before start date");
            }
        } catch (e) {
             errors.push("Invalid date format. Please use YYYY-MM.");
             console.error("Date parsing error:", e);
        }
    } else if (!editedRole.startDate) {
         errors.push("Start date is required"); // Ensure start date exists even if end date doesn't
    }


    setValidationErrors(errors);
    const isValid = errors.length === 0;
    onValidate(isValid); // Notify parent immediately about validation status change
    return isValid;
  };

  // Handle saving the edited role
  const handleSave = () => {
    if (validateRole()) { // Validate before saving
      onEdit(editedRole); // Pass the edited data up
      setIsEditing(false);
      // onValidate(true); // Already called within validateRole
    } else {
       // onValidate(false); // Already called within validateRole
    }
  };

  // Reset local state when the input 'role' prop changes
  // Reset local state when the input 'role' prop changes, but ONLY if not currently editing.
  // If editing, the user's changes should persist until saved or cancelled.
  useEffect(() => {
    // Only reset the form content if NOT currently editing.
    // This prevents wiping user input if the parent re-renders while the form is open.
    if (!isEditing) {
        setEditedRole(role);
        setValidationErrors([]); // Clear previous errors
        onValidate(true); // Assume valid initially when prop changes or editing is cancelled
    }
    // NOTE: We intentionally DO NOT reset isEditing here based on prop changes.
    // isEditing should only change based on user interaction (Edit/Save/Cancel buttons).
  }, [role, onValidate, isEditing]); // Add isEditing to dependency array to control when this effect runs

  // Handle changes in form inputs
   const handleChange = (field, value) => {
    setEditedRole(prev => ({ ...prev, [field]: value }));
    // Optional: Trigger validation on each change for immediate feedback
    // validateRole(); // This might be too noisy, validate on save is usually sufficient
  };

  // Handle changes for array fields (textarea split by newline)
  const handleArrayChange = (field, value) => {
     handleChange(field, value.split('\n').map(s => s.trim()).filter(Boolean));
  };

  // Helper function to format YYYY-MM dates for display (Moved to correct scope)
  const formatDateForDisplay = (dateString) => {
    if (!dateString || typeof dateString !== 'string' || !dateString.match(/^\d{4}-\d{2}$/)) {
      return dateString; // Return original if invalid or not in expected format
    }
    try {
      // Parse YYYY-MM string (treat as UTC to avoid timezone issues)
      const date = parse(dateString, 'yyyy-MM', new Date());
      // Add UTC marker to ensure formatting happens in UTC
      const utcDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
      return format(utcDate, 'MMMM yyyy'); // Format as "Month YYYY"
    } catch (error) {
      console.error("Error formatting date:", dateString, error);
      return dateString; // Return original on error
    }
  };

  // Handle changes for comma-separated fields (skills)
   const handleCommaSeparatedChange = (field, value) => {
     handleChange(field, value.split(',').map(s => s.trim()).filter(Boolean));
   };


  return (
    <div className="border border-huntd-secondary/20 rounded-lg p-4 bg-white shadow-sm">
      {!isEditing ? (
        // --- Display Mode ---
        <>
          <div className="flex justify-between items-start mb-4">
            <div>
              <h4 className="font-medium text-huntd-primary">{editedRole.role || 'Role Title Missing'}</h4>
              <p className="text-sm text-huntd-secondary">{editedRole.company || 'Company Missing'}</p>
              <p className="text-sm text-huntd-secondary">
                {formatDateForDisplay(editedRole.startDate) || 'Start Date Missing'} - {editedRole.endDate ? formatDateForDisplay(editedRole.endDate) : 'Present'}
              </p>
            </div>
            {/* Corrected Button Group */}
            <div className="flex gap-2 items-center">
              <SecondaryButton onClick={() => setIsEditing(true)} size="sm" className="!px-2 !py-1">
                <Edit2 size={14} className="mr-1" /> Edit
              </SecondaryButton>
              <DangerButton onClick={onDelete} size="sm" className="!px-2 !py-1">
                <Trash2 size={14} className="mr-1" /> Delete
              </DangerButton>
            </div>
            {/* End Corrected Button Group */}
          </div>
          {editedRole.description && ( // Use 'description'
            <p className="text-sm text-huntd-secondary/80 my-3">{editedRole.description}</p>
          )}
          {editedRole.responsibilities?.length > 0 && (
            <div className="mb-3">
              <h5 className="text-xs font-semibold text-huntd-primary uppercase tracking-wide mb-1">Responsibilities</h5>
              <ul className="list-disc list-inside text-sm text-huntd-secondary/80 space-y-1">
                {editedRole.responsibilities.map((resp, i) => <li key={`resp-${i}`}>{resp}</li>)}
              </ul>
            </div>
          )}
          {editedRole.achievements?.length > 0 && ( // Use 'achievements'
            <div className="mb-3">
              <h5 className="text-xs font-semibold text-huntd-primary uppercase tracking-wide mb-1">Achievements</h5>
              <ul className="list-disc list-inside text-sm text-huntd-secondary/80 space-y-1">
                {editedRole.achievements.map((ach, i) => <li key={`ach-${i}`}>{ach}</li>)} {/* Use 'achievements' */}
              </ul>
            </div>
          )}
          {/* Technical Skills Display */}
          {editedRole.technicalSkills?.length > 0 && (
             <div className="mb-3">
               <h5 className="text-xs font-semibold text-huntd-primary uppercase tracking-wide mb-1">Technical Skills</h5>
               <div className="flex flex-wrap gap-2 mt-1">
                 {editedRole.technicalSkills.map((skill, i) => (
                   <span key={`tech-skill-${i}`} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                     {skill}
                   </span>
                 ))}
               </div>
             </div>
          )}
          {/* Soft Skills Display */}
          {editedRole.softSkills?.length > 0 && (
             <div className="mb-3">
               <h5 className="text-xs font-semibold text-huntd-primary uppercase tracking-wide mb-1">Soft Skills</h5>
               <div className="flex flex-wrap gap-2 mt-1">
                 {editedRole.softSkills.map((skill, i) => (
                   <span key={`soft-skill-${i}`} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                     {skill}
                   </span>
                 ))}
               </div>
             </div>
          )}
          {/* Industry Knowledge Display */}
          {editedRole.industryKnowledge?.length > 0 && (
             <div className="mb-3">
               <h5 className="text-xs font-semibold text-huntd-primary uppercase tracking-wide mb-1">Industry Knowledge</h5>
               <div className="flex flex-wrap gap-2 mt-1">
                 {editedRole.industryKnowledge.map((skill, i) => (
                   <span key={`ind-skill-${i}`} className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                     {skill}
                   </span>
                 ))}
               </div>
             </div>
          )}
        </>
      ) : (
        // --- Edit Mode ---
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Company"
              value={editedRole.company || ''}
              onChange={(e) => handleChange('company', e.target.value)}
              required
            />
            <FormInput
              label="Role"
              value={editedRole.role || ''}
              onChange={(e) => handleChange('role', e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Start Date"
              type="month" // HTML month input expects YYYY-MM
              value={editedRole.startDate || ''}
              onChange={(e) => handleChange('startDate', e.target.value)}
              required
            />
            <FormInput
              label="End Date"
              type="month" // HTML month input expects YYYY-MM
              value={editedRole.endDate || ''}
              onChange={(e) => handleChange('endDate', e.target.value)}
              // Not strictly required, can be empty for current role
            />
          </div>
          <FormTextArea
            label="Role Overview"
            value={editedRole.description || ''} // Use 'description'
            onChange={(e) => handleChange('description', e.target.value)} // Use 'description'
            rows={3}
            placeholder="Brief summary of the role"
          />
          <FormTextArea
            label="Responsibilities (one per line)"
            value={Array.isArray(editedRole.responsibilities) ? editedRole.responsibilities.join('\n') : ''}
            onChange={(e) => handleArrayChange('responsibilities', e.target.value)}
            rows={5}
            placeholder="List key responsibilities, one per line"
            required
          />
           <FormTextArea
            label="Impact / Achievements (one per line)"
            value={Array.isArray(editedRole.achievements) ? editedRole.achievements.join('\n') : ''} // Use 'achievements'
            onChange={(e) => handleArrayChange('achievements', e.target.value)} // Use 'achievements'
            rows={5}
            placeholder="List key impacts or achievements, one per line"
          />
          {/* Categorized Skills Inputs */}
          <FormInput
            label="Technical Skills (comma-separated)"
            value={Array.isArray(editedRole.technicalSkills) ? editedRole.technicalSkills.join(', ') : ''}
            onChange={(e) => handleCommaSeparatedChange('technicalSkills', e.target.value)}
            placeholder="e.g., JavaScript, Python, AWS, Docker"
          />
          <FormInput
            label="Soft Skills (comma-separated)"
            value={Array.isArray(editedRole.softSkills) ? editedRole.softSkills.join(', ') : ''}
            onChange={(e) => handleCommaSeparatedChange('softSkills', e.target.value)}
            placeholder="e.g., Leadership, Communication, Teamwork"
          />
          <FormInput
            label="Industry Knowledge (comma-separated)"
            value={Array.isArray(editedRole.industryKnowledge) ? editedRole.industryKnowledge.join(', ') : ''}
            onChange={(e) => handleCommaSeparatedChange('industryKnowledge', e.target.value)}
            placeholder="e.g., Financial Services, Healthcare, E-commerce"
          />

          {validationErrors.length > 0 && (
            <ValidationError errors={validationErrors} />
          )}

          <div className="flex justify-end gap-2 mt-4">
            <SecondaryButton onClick={() => {
              setEditedRole(role); // Reset changes
              setIsEditing(false);
              setValidationErrors([]); // Clear errors
              onValidate(true); // Assume valid again on cancel
            }}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={handleSave}>
              Save Changes
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
};

ExtractedRoleCard.propTypes = {
  role: PropTypes.shape({
    company: PropTypes.string,
    role: PropTypes.string,
    startDate: PropTypes.string, // Expecting YYYY-MM format
    endDate: PropTypes.string,   // Expecting YYYY-MM format
    description: PropTypes.string, // Use 'description'
    responsibilities: PropTypes.arrayOf(PropTypes.string),
    achievements: PropTypes.arrayOf(PropTypes.string), // Use 'achievements'
    // skills removed, replaced by categorized skills below
    technicalSkills: PropTypes.arrayOf(PropTypes.string),
    softSkills: PropTypes.arrayOf(PropTypes.string),
    industryKnowledge: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired // Add prop type for delete handler
};

export default ExtractedRoleCard;
